import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { FiUser, FiMail, FiPhone, FiLock, FiArrowLeft, FiCheck } from 'react-icons/fi';

// Configure axios defaults
axios.defaults.baseURL = process.env.REACT_APP_API_URL;


const RegisterPage = () => {
  const [step, setStep] = useState(1); // 1: Registration, 2: OTP Verification
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    phone: '',
    password: '',
    rememberMe: false,
    is_member: false  // Add this
  });
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const navigate = useNavigate();

  // Check if user is already logged in
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      navigate('/');
    }
  }, [navigate]);

  // Countdown timer
  useEffect(() => {
    let timer;
    if (timerActive && countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else if (countdown === 0) {
      setTimerActive(false);
    }
    return () => clearTimeout(timer);
  }, [countdown, timerActive]);

  const startCountdown = (seconds) => {
    setCountdown(seconds);
    setTimerActive(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleOtpChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      
      // Auto-focus next input
      if (value && index < 5) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  // Register User - Generate OTP
 const handleSubmit = async (e) => {
  e.preventDefault();

  //  IF NOT A MEMBER → REDIRECT DIRECTLY
  if (!formData.is_member) {
    window.location.href = 'https://www.marketmaestroo.com/';
    return; //  stop further execution
  }

  // ✅ MEMBER FLOW CONTINUES AS IT IS
  setLoading(true);
  setError('');
  setSuccess('');

  try {
    const response = await axios.post('/api/auth/register', {
      username: formData.username,
      email: formData.email,
      phone: formData.phone,
      password: formData.password,
      is_member: formData.is_member
    });

    if (response.data.success) {
      setStep(2);
      startCountdown(600);
      setSuccess('OTP sent to your email. Please verify to continue.');
    }
  } catch (err) {
    setError(err.response?.data?.error || 'Registration failed. Please try again.');
  } finally {
    setLoading(false);
  }
};


  // Verify OTP and complete registration
  const verifyOTP = async () => {
    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      setError('Please enter 6-digit OTP');
      return;
    }

    setOtpLoading(true);
    setError('');

    try {
      // Send to /api/auth/verify-register
      const response = await axios.post('/api/auth/verify-register', {
        email: formData.email,
        otp: otpCode
      });

      if (response.data.success) {
        const { tokens, user } = response.data.data;
        
        // Save tokens and user info
        localStorage.setItem('accessToken', tokens.accessToken);
        localStorage.setItem('refreshToken', tokens.refreshToken);
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('rememberMe', formData.rememberMe.toString());
        
        // Set axios default header
        axios.defaults.headers.common['Authorization'] = `Bearer ${tokens.accessToken}`;
        
        setSuccess('Registration successful! Redirecting...');
        
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 1500);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid OTP. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  // Resend OTP
  const resendOTP = async () => {
    setOtpLoading(true);
    setError('');

    try {
      // Send to /api/auth/resend-otp
      const response = await axios.post('/api/auth/resend-otp', {
        email: formData.email
      });

      if (response.data.success) {
        setSuccess('New OTP sent successfully!');
        startCountdown(600); // Restart 10 minute timer
        setOtp(['', '', '', '', '', '']); // Clear OTP fields
        // Focus first OTP input
        setTimeout(() => {
          const firstInput = document.getElementById('otp-0');
          if (firstInput) firstInput.focus();
        }, 100);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resend OTP. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  // JSX remains exactly the same...
  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center justify-center space-x-4">
              <div className={`flex items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                  1
                </div>
                <span className="ml-2 font-medium">Registration</span>
              </div>
              <div className="w-24 h-1 bg-gray-300"></div>
              <div className={`flex items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                  2
                </div>
                <span className="ml-2 font-medium">Verify OTP</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="md:flex">
              {/* Left Side - Illustration/Info */}
              <div className="hidden md:block md:w-2/5 bg-gradient-to-br from-blue-600 to-blue-600 p-10">
                <div className="h-full flex flex-col justify-center">
                  <div className="text-white">
                    <h2 className="text-3xl font-bold mb-6">Join Our Community</h2>
                    <div className="space-y-6">
                      <div className="flex items-start">
                        <svg className="text-white text-xl mt-1 mr-3 w-10" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <h4 className="font-semibold">Secure Account</h4>
                          <p className="text-blue-100 text-sm">End-to-end encrypted security</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <svg className="text-white text-xl mt-1 mr-3 w-10" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <h4 className="font-semibold">Instant Verification</h4>
                          <p className="text-blue-100 text-sm">One-time password protection</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <svg className="text-white text-xl mt-1 mr-3 w-10" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <h4 className="font-semibold">Premium Features</h4>
                          <p className="text-blue-100 text-sm">Access to all premium features</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-8">
                    <div className="w-64 h-64 mx-auto bg-gradient-to-br from-blue-400 to-purple-400 rounded-full opacity-20"></div>
                  </div>
                </div>
              </div>

              {/* Right Side - Form */}
              <div className="md:w-3/5 p-8 md:p-12">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold text-gray-900">
                    {step === 1 ? 'Create Your Account' : 'Verify Your Email'}
                  </h2>
                  <p className="text-gray-600 mt-2">
                    {step === 1 
                      ? 'Fill in your details to get started'
                      : `Enter the 6-digit code sent to ${formData.email}`
                    }
                  </p>
                </div>

                {/* Error/Success Messages */}
                {error && (
                  <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-red-700 font-medium">{error}</p>
                      </div>
                    </div>
                  </div>
                )}

                {success && (
                  <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 rounded-lg">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-green-700 font-medium">{success}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 1: Registration Form */}
                {step === 1 && (
                  <form className="space-y-6" onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Username */}
                      <div className="space-y-2">
                        <div className="relative">
                          <input
                            id="username"
                            name="username"
                            type="text"
                            required
                            minLength="3"
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                            placeholder="Your Name"
                            value={formData.username}
                            onChange={handleChange}
                            disabled={loading}
                          />
                          <FiUser className="absolute left-3 top-3.5 text-gray-400" />
                        </div>
                      </div>

                      {/* Email */}
                      <div className="space-y-2">
                        <div className="relative">
                          <input
                            id="email"
                            name="email"
                            type="email"
                            required
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                            placeholder="Your Email"
                            value={formData.email}
                            onChange={handleChange}
                            disabled={loading}
                          />
                          <FiMail className="absolute left-3 top-3.5 text-gray-400" />
                        </div>
                      </div>

                      {/* Phone */}
                      <div className="space-y-2">
                        <div className="relative">
                          <input
                            id="phone"
                            name="phone"
                            type="tel"
                            required
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                            placeholder="Your Phone Number"
                            value={formData.phone}
                            onChange={handleChange}
                            disabled={loading}
                          />
                          <FiPhone className="absolute left-3 top-3.5 text-gray-400" />
                        </div>
                      </div>

                      {/* Password */}
                      <div className="space-y-2">
                        <div className="relative">
                          <input
                            id="password"
                            name="password"
                            type="password"
                            required
                            minLength="8"
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                            placeholder="••••••••"
                            value={formData.password}
                            onChange={handleChange}
                            disabled={loading}
                          />
                          <FiLock className="absolute left-3 top-3.5 text-gray-400" />
                        </div>
                      </div>
                    </div>

                    {/* Remember Me */}
                    <div className="flex items-center">
                      <input
                        id="rememberMe"
                        name="rememberMe"
                        type="checkbox"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        checked={formData.rememberMe}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      <label htmlFor="rememberMe" className="ml-2 text-sm text-gray-700">
                        Remember me for 30 days
                      </label>
                    </div>

                    {/* Membership Toggle */}
                    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-gray-50">
                      <div>
                        <h4 className="font-semibold text-gray-800">Maestroo Member</h4>
                        <p className="text-sm text-gray-600">Get access to exclusive features and benefits</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          is_member: !formData.is_member
                        })}
                        disabled={loading}
                        className={`
                          relative inline-flex h-7 w-14 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                          transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 
                          focus:ring-offset-2 ${formData.is_member ? 'bg-blue-600' : 'bg-gray-300'}
                        `}
                      >
                        <span
                          className={`
                            pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-lg 
                            ring-0 transition duration-200 ease-in-out
                            ${formData.is_member ? 'translate-x-7' : 'translate-x-0'}
                          `}
                        >
                          {formData.is_member && (
                            <FiCheck className="h-4 w-4 text-blue-600 mx-auto mt-1" />
                          )}
                        </span>
                      </button>
                    </div>

                    <div className="text-center">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${formData.is_member ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                        {formData.is_member ? (
                          <>
                            <FiCheck className="mr-1.5" />
                            Member Status: Yes
                          </>
                        ) : (
                          'Member Status: No'
                        )}
                      </span>
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3.5 px-4 bg-gradient-to-r from-blue-600 to-blue-600 hover:from-blue-700 hover:to-blue-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <div className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Creating Account...
                        </div>
                      ) : (
                        'Continue to Verification'
                      )}
                    </button>
                  </form>
                )}

                {/* Step 2: OTP Verification */}
                {step === 2 && (
                  <div className="space-y-8">
                    {/* OTP Input Fields */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-4">
                        Enter 6-digit verification code
                      </label>
                      <div className="flex justify-center space-x-3">
                        {otp.map((digit, index) => (
                          <input
                            key={index}
                            id={`otp-${index}`}
                            type="text"
                            inputMode="numeric"
                            maxLength="1"
                            value={digit}
                            onChange={(e) => handleOtpChange(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            className="w-14 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300"
                            disabled={otpLoading}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Timer */}
                    <div className="text-center">
                      {timerActive ? (
                        <p className="text-sm text-gray-600">
                          Code expires in{' '}
                          <span className="font-semibold text-blue-600">
                            {Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, '0')}
                          </span>
                        </p>
                      ) : (
                        <p className="text-sm text-red-600 font-medium">
                          OTP has expired
                        </p>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="space-y-4">
                      <button
                        onClick={verifyOTP}
                        disabled={otpLoading || otp.join('').length !== 6}
                        className="w-full py-3.5 px-4 bg-gradient-to-r from-blue-600 to-blue-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {otpLoading ? (
                          <div className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Verifying...
                          </div>
                        ) : (
                          'Verify & Continue'
                        )}
                      </button>

                      <button
                        onClick={resendOTP}
                        disabled={timerActive || otpLoading}
                        className="w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {timerActive ? `Resend OTP (${Math.floor(countdown / 60)}:${String(countdown % 60).padStart(2, '0')})` : 'Resend OTP'}
                      </button>

                      <button
                        onClick={() => setStep(1)}
                        className="w-full py-3 px-4 text-blue-600 hover:text-blue-700 font-medium rounded-lg hover:bg-blue-50 transition-colors duration-300 flex items-center justify-center"
                      >
                        <FiArrowLeft className="mr-2" />
                        Back to Registration
                      </button>
                    </div>
                  </div>
                )}

                {/* Bottom Links */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0">
                    <p className="text-sm text-gray-600">
                      Already have an account?{' '}
                      <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
                        Sign in here
                      </Link>
                    </p>
                    <Link to="/" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900">
                      <FiArrowLeft className="mr-2" />
                      Back to Home
                    </Link>
                  </div>
                </div>

                {/* Security Note */}
                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-700 text-center">
                    <span className="font-semibold">Note:</span> Your information is protected with 256-bit SSL encryption. 
                    We'll never share your details with third parties.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RegisterPage;